import { useState, useEffect } from 'react';
import { useAuth, useUser, SignIn, SignUp } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getInviteInfo, joinWorkspace, setTokenGetter } from '@/lib/api';
import { toast } from 'sonner';
import { Users, CheckCircle, XCircle, Loader2 } from 'lucide-react';

export const JoinWorkspace = ({ token, onComplete }) => {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const [joined, setJoined] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('signup');

  // Load invite info
  useEffect(() => {
    async function loadInvite() {
      try {
        const res = await getInviteInfo(token);
        setInviteInfo(res.data);
      } catch (err) {
        console.error('Failed to load invite:', err);
        setError(err.response?.data?.detail || 'Invalid or expired invite link');
      } finally {
        setLoading(false);
      }
    }
    loadInvite();
  }, [token]);

  // Auto-join when user signs in
  useEffect(() => {
    async function handleJoin() {
      if (isLoaded && isSignedIn && inviteInfo && !joined && !joining) {
        setJoining(true);
        try {
          // Set up auth token
          setTokenGetter(getToken);
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Join workspace
          await joinWorkspace(token);
          setJoined(true);
          toast.success('Successfully joined the workspace!');
          
          // Redirect after short delay
          setTimeout(() => {
            if (onComplete) {
              onComplete();
            } else {
              window.location.href = '/';
            }
          }, 1500);
        } catch (err) {
          console.error('Failed to join:', err);
          setError(err.response?.data?.detail || 'Failed to join workspace');
        } finally {
          setJoining(false);
        }
      }
    }
    handleJoin();
  }, [isLoaded, isSignedIn, inviteInfo, joined, joining, getToken, token, onComplete]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-2" />
            <CardTitle>Invalid Invite</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => window.location.href = '/'}>
              Go to Cash Forth
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (joined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
            <CardTitle>Welcome!</CardTitle>
            <CardDescription>
              You've joined {inviteInfo?.workspace_name}. Redirecting...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (joining) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-2" />
            <CardTitle>Joining Workspace...</CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Show auth if user needs to sign in
  if (showAuth || (isLoaded && !isSignedIn)) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-md mx-auto space-y-6 pt-8">
          <Card>
            <CardHeader className="text-center">
              <Users className="w-10 h-10 text-primary mx-auto mb-2" />
              <CardTitle>Join {inviteInfo?.workspace_name}</CardTitle>
              <CardDescription>
                You've been invited to collaborate on a cash flow forecast.
                {authMode === 'signup' ? ' Create an account to continue.' : ' Sign in to continue.'}
              </CardDescription>
            </CardHeader>
          </Card>

          <div className="bg-card rounded-lg border p-4">
            {authMode === 'signup' ? (
              <SignUp routing="hash" afterSignUpUrl={window.location.href} />
            ) : (
              <SignIn routing="hash" afterSignInUrl={window.location.href} />
            )}
          </div>

          <p className="text-center text-sm text-muted-foreground">
            {authMode === 'signup' ? (
              <>
                Already have an account?{' '}
                <button
                  onClick={() => setAuthMode('signin')}
                  className="text-primary hover:underline"
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                Don't have an account?{' '}
                <button
                  onClick={() => setAuthMode('signup')}
                  className="text-primary hover:underline"
                >
                  Sign up
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    );
  }

  // Show invite info and join button
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <Users className="w-12 h-12 text-primary mx-auto mb-2" />
          <CardTitle>Join {inviteInfo?.workspace_name}</CardTitle>
          <CardDescription>
            You've been invited to collaborate on a cash flow forecast.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 rounded-lg bg-muted text-sm">
            <p><strong>Invite for:</strong> {inviteInfo?.email}</p>
          </div>
          <Button className="w-full" onClick={() => setShowAuth(true)}>
            Continue
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
